import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card } from '@/components/ui/card';
import { ChevronDown, ChevronRight, Loader2, Sparkles, Copy } from 'lucide-react';
import { toast } from 'sonner';
import {
  createFurnitureProject,
  duplicateFurnitureProject,
  getFurnitureSubProjects,
  getFurnitureProject,
} from '@/lib/api';

/**
 * Multi-purpose wizard for creating sub-projects.
 *
 * Modes:
 *  - mode="add"      → adding a new unit to a master. Optional source picker
 *                      shows the master's sibling units to clone from.
 *  - mode="duplicate" → duplicating a specific sub-project (source is fixed,
 *                       still editable via the picker).
 *
 * Returns the freshly-created project via `onCreated(newProject)`.
 */
export function SubProjectWizard({ open, onOpenChange, master, mode = 'add', sourceProject = null, onCreated }) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [sourceId, setSourceId] = useState(''); // '' = blank
  const [sourceProj, setSourceProj] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [loadingSiblings, setLoadingSiblings] = useState(false);
  const [loadingSource, setLoadingSource] = useState(false);
  const [saving, setSaving] = useState(false);
  // selections: { [room_id]: { include: bool, name: str, items: { [item_id]: bool } } }
  const [selections, setSelections] = useState({});
  const [expandedRooms, setExpandedRooms] = useState(() => new Set());

  // Reset state when dialog opens
  useEffect(() => {
    if (!open) return;
    setName('');
    setAddress(master?.address || '');
    setSourceId(sourceProject?.id || '');
    setSourceProj(null);
    setSelections({});
    setExpandedRooms(new Set());
  }, [open, master?.address, sourceProject?.id]);

  // Load siblings of the master (for the source picker)
  useEffect(() => {
    if (!open || !master?.id) return;
    setLoadingSiblings(true);
    getFurnitureSubProjects(master.id)
      .then(list => setSiblings(list.filter(p => !p.archived && p.id !== sourceProject?.id)))
      .catch(() => setSiblings([]))
      .finally(() => setLoadingSiblings(false));
  }, [open, master?.id, sourceProject?.id]);

  // Load full source project (with rooms/items) whenever sourceId changes
  useEffect(() => {
    if (!open) return;
    if (!sourceId) { setSourceProj(null); setSelections({}); return; }
    setLoadingSource(true);
    getFurnitureProject(sourceId)
      .then(p => {
        setSourceProj(p);
        // Default: include every room and every item (the wizard's job is to
        // let the user *un-check* what's different, not start from nothing).
        const sel = {};
        for (const r of (p.rooms || [])) {
          const items = {};
          for (const it of (r.items || [])) items[it.id] = true;
          sel[r.id] = { include: true, name: r.name || '', items };
        }
        setSelections(sel);
        setExpandedRooms(new Set((p.rooms || []).map(r => r.id)));
      })
      .catch(() => { setSourceProj(null); setSelections({}); })
      .finally(() => setLoadingSource(false));
  }, [open, sourceId]);

  const summary = useMemo(() => {
    if (!sourceProj) return { rooms: 0, items: 0 };
    let rooms = 0, items = 0;
    for (const r of (sourceProj.rooms || [])) {
      const s = selections[r.id];
      if (!s || !s.include) continue;
      rooms += 1;
      for (const it of (r.items || [])) {
        if (s.items[it.id]) items += 1;
      }
    }
    return { rooms, items };
  }, [sourceProj, selections]);

  const toggleRoomExpand = (rid) => setExpandedRooms(prev => {
    const next = new Set(prev);
    if (next.has(rid)) next.delete(rid); else next.add(rid);
    return next;
  });

  const toggleRoomInclude = (rid, value) => setSelections(prev => ({
    ...prev,
    [rid]: { ...prev[rid], include: value },
  }));

  const renameRoom = (rid, newName) => setSelections(prev => ({
    ...prev,
    [rid]: { ...prev[rid], name: newName },
  }));

  const toggleItem = (rid, iid, value) => setSelections(prev => ({
    ...prev,
    [rid]: { ...prev[rid], items: { ...prev[rid].items, [iid]: value } },
  }));

  const setAllItems = (rid, value) => setSelections(prev => {
    const room = prev[rid];
    if (!room) return prev;
    const items = {};
    for (const k of Object.keys(room.items)) items[k] = value;
    return { ...prev, [rid]: { ...room, items } };
  });

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Enter a name for the new unit'); return; }
    setSaving(true);
    try {
      let created;
      if (!sourceId) {
        // Blank: just create a regular sub-project
        created = await createFurnitureProject({
          name: name.trim(),
          address: address.trim(),
          parent_id: master?.id || undefined,
        });
      } else {
        // Build rooms_filter from selections
        const rooms_filter = (sourceProj?.rooms || []).reduce((acc, r) => {
          const s = selections[r.id];
          if (!s || !s.include) return acc;
          const item_ids = Object.entries(s.items).filter(([, v]) => v).map(([k]) => k);
          acc.push({
            source_room_id: r.id,
            name: (s.name || r.name || '').trim() || r.name || 'Room',
            item_ids,
          });
          return acc;
        }, []);
        created = await duplicateFurnitureProject(sourceId, {
          name: name.trim(),
          address: address.trim(),
          parent_id: master?.id || undefined,
          rooms_filter,
        });
      }
      toast.success(`Created "${created.name}"`);
      onCreated?.(created);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create');
    } finally {
      setSaving(false);
    }
  };

  const sourceOptions = useMemo(() => {
    // Include the duplicate-source project first if provided
    const opts = [];
    if (sourceProject) opts.push(sourceProject);
    for (const s of siblings) {
      if (s.id !== sourceProject?.id) opts.push(s);
    }
    return opts;
  }, [siblings, sourceProject]);

  const titleText = mode === 'duplicate'
    ? `Duplicate "${sourceProject?.name || 'Unit'}"`
    : master ? `Add Unit to "${master.name}"` : 'New Unit';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] flex flex-col gap-0"
        data-testid="sub-project-wizard"
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#2d948c]" />
            {titleText}
          </DialogTitle>
          <DialogDescription>
            Choose a unit to clone from (or start blank), then pick which rooms and items should carry over.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-3 space-y-4">
          {/* Name + Address */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium">Unit name *</Label>
              <Input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Unit 101"
                autoFocus
                data-testid="wizard-name-input"
              />
            </div>
            <div>
              <Label className="text-xs font-medium">Address</Label>
              <Input
                value={address}
                onChange={e => setAddress(e.target.value)}
                placeholder="Pre-filled from master"
                data-testid="wizard-address-input"
              />
            </div>
          </div>

          {/* Source picker */}
          <div>
            <Label className="text-xs font-medium">Clone from</Label>
            <Select
              value={sourceId || 'blank'}
              onValueChange={(v) => setSourceId(v === 'blank' ? '' : v)}
            >
              <SelectTrigger data-testid="wizard-source-select">
                <SelectValue placeholder="Start blank" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="blank">
                  <span className="text-[#5b6773]">Start blank (no rooms/items)</span>
                </SelectItem>
                {sourceOptions.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {loadingSiblings && <p className="text-[10px] text-[#5b6773] mt-1">Loading siblings…</p>}
          </div>

          {/* Diff: rooms & items */}
          {sourceId && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-medium">Pick what carries over</Label>
                <p className="text-[10px] text-[#5b6773]">
                  {summary.rooms} room{summary.rooms === 1 ? '' : 's'} · {summary.items} item{summary.items === 1 ? '' : 's'}
                </p>
              </div>
              {loadingSource ? (
                <div className="py-8 text-center text-sm text-[#5b6773]">
                  <Loader2 className="inline h-4 w-4 animate-spin mr-2" />
                  Loading rooms…
                </div>
              ) : !sourceProj || (sourceProj.rooms || []).length === 0 ? (
                <Card className="p-4 text-sm text-[#5b6773] italic text-center">
                  Source has no rooms yet — the new unit will start empty.
                </Card>
              ) : (
                <div className="space-y-2">
                  {(sourceProj.rooms || []).map(room => {
                    const s = selections[room.id] || { include: false, items: {} };
                    const isOpen = expandedRooms.has(room.id);
                    const itemKeys = Object.keys(s.items || {});
                    const includedItemCount = itemKeys.filter(k => s.items[k]).length;
                    return (
                      <Card key={room.id} className={s.include ? '' : 'opacity-60'} data-testid={`wizard-room-${room.id}`}>
                        <div className="flex items-center gap-2 px-3 py-2">
                          <Checkbox
                            checked={s.include}
                            onCheckedChange={(v) => toggleRoomInclude(room.id, !!v)}
                            data-testid={`wizard-room-include-${room.id}`}
                          />
                          <button
                            type="button"
                            onClick={() => toggleRoomExpand(room.id)}
                            className="flex-shrink-0 text-[#5b6773]"
                            data-testid={`wizard-room-expand-${room.id}`}
                          >
                            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </button>
                          <Input
                            value={s.name || ''}
                            onChange={e => renameRoom(room.id, e.target.value)}
                            disabled={!s.include}
                            className="h-7 text-sm flex-1"
                            data-testid={`wizard-room-name-${room.id}`}
                          />
                          <span className="text-[10px] text-[#5b6773] tabular-nums flex-shrink-0">
                            {includedItemCount} / {itemKeys.length}
                          </span>
                        </div>
                        {isOpen && s.include && (
                          <div className="px-3 pb-3 pt-1 border-t border-border/40">
                            <div className="flex gap-2 mb-2">
                              <button
                                type="button"
                                onClick={() => setAllItems(room.id, true)}
                                className="text-[10px] text-[#2d948c] hover:underline"
                                data-testid={`wizard-items-all-${room.id}`}
                              >
                                Select all
                              </button>
                              <button
                                type="button"
                                onClick={() => setAllItems(room.id, false)}
                                className="text-[10px] text-[#5b6773] hover:underline"
                                data-testid={`wizard-items-none-${room.id}`}
                              >
                                Skip all
                              </button>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                              {(room.items || []).map(it => {
                                const checked = !!s.items[it.id];
                                const hasBudget = it.budget != null && it.budget !== '';
                                return (
                                  <label
                                    key={it.id}
                                    className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded border ${checked ? 'border-[#2d948c]/30 bg-[#2d948c]/[0.04]' : 'border-border/40'} cursor-pointer hover:bg-muted/30`}
                                    data-testid={`wizard-item-${it.id}`}
                                  >
                                    <Checkbox
                                      checked={checked}
                                      onCheckedChange={(v) => toggleItem(room.id, it.id, !!v)}
                                    />
                                    <span className="flex-1 truncate">{it.name || '(unnamed)'}</span>
                                    {hasBudget && (
                                      <span className="text-[10px] text-[#5b6773] tabular-nums flex-shrink-0">
                                        ${Number(it.budget).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                      </span>
                                    )}
                                  </label>
                                );
                              })}
                              {(room.items || []).length === 0 && (
                                <p className="text-xs text-[#5b6773] italic col-span-2">No items in this room.</p>
                              )}
                            </div>
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              )}
              <p className="text-[10px] text-[#5b6773] mt-2 leading-relaxed">
                <strong>Unchecked items</strong> won&apos;t be copied to the new unit, so you can customize each unit
                independently from here. Item budgets and specs are carried over; <strong>ordered totals reset</strong>.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 border-t pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="gap-1.5"
            data-testid="wizard-save-btn"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
            {sourceId ? `Create from "${sourceProj?.name || 'source'}"` : 'Create blank'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
